// ============================================
// API Client
// ============================================

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiError } from '../types';

// Vite env type declaration
/// <reference types="vite/client" />

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('admin');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================
// Auth API
// ============================================
export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ access_token: string; token_type: string }>(
      '/auth/login',
      new URLSearchParams({ username: email, password }),
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    ),

  me: () => api.get<Admin>('/auth/me'),

  register: (data: { email: string; password: string; full_name?: string }) =>
    api.post<Admin>('/auth/register', data),

  updateMe: (data: { full_name?: string }) =>
    api.patch<Admin>('/auth/me', data),
};

// ============================================
// Sub-Account API
// ============================================
export const subAccountApi = {
  list: (skip = 0, limit = 100) =>
    api.get<SubAccount[]>(`/sub-accounts?skip=${skip}&limit=${limit}`),

  get: (id: string) =>
    api.get<SubAccount>(`/sub-accounts/${id}`),

  getDetail: (id: string) =>
    api.get<SubAccountDetail>(`/sub-accounts/${id}/detail`),

  getStats: (id: string) =>
    api.get<SubAccountStats>(`/sub-accounts/${id}/stats`),

  create: (data: any) =>
    api.post<SubAccount>('/sub-accounts', data),

  update: (id: string, data: any) =>
    api.patch<SubAccount>(`/sub-accounts/${id}`, data),

  delete: (id: string) =>
    api.delete(`/sub-accounts/${id}`),

  activateBot: (id: string) =>
    api.post<SubAccount>(`/sub-accounts/${id}/activate-bot`),

  deactivateBot: (id: string) =>
    api.post<SubAccount>(`/sub-accounts/${id}/deactivate-bot`),

  getGhlConversations: (id: string, query = '') => {
    const params = new URLSearchParams({ limit: '100' });
    if (query.trim()) params.set('query', query.trim());
    return api.get<{ conversations: GHLConversation[]; total: number; sampled_count: number; source: string }>(
      `/admin/sub-accounts/${id}/conversations?${params}`);
  },

  getGhlMessages: (id: string, conversationId: string) =>
    api.get<GHLMessage[]>(`/admin/sub-accounts/${id}/conversations/${encodeURIComponent(conversationId)}/messages?limit=100`),
};

// ============================================
// Dashboard API
// ============================================
export const dashboardApi = {
  getOverview: () =>
    api.get<DashboardData>('/admin/dashboard'),
  getAgentOverview: () =>
    api.get<AgentOverviewData>('/admin/agent-overview'),
};

// Re-export types for convenience
import type { Admin, SubAccount, SubAccountDetail, SubAccountStats, DashboardData, AgentOverviewData, GHLConversation, GHLMessage } from '../types';